import { DialogTitle, FormControl, FormControlLabel, FormHelperText, Link, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { RegistApplySchoolDecision } from 'API';
import React, { FC } from 'react';
import { ErrorData } from './SchoolDecisionApplicationDialogErrorData';

export interface Props {
  errorAttribute: ErrorData;
  dialogOpen: boolean;
  inputData: RegistApplySchoolDecision;
  reservedDays: string[];
  handleChangeReservedDay: (_: boolean, idx: number) => void;
  handleChangeSchoolConfirmationDoc: (_: React.ChangeEvent<HTMLInputElement>) => void;
  handleDeleteSchoolConfirmationDoc: () => void;
  handleClose: () => void;
  handleRegist: () => void;
}

const styles = {
  categoryHeader: {
    fontWeight: 'bold',
  },
  itemHeader: {
    backgroundColor: '#ececec',
    fontWeight: 'bold',
  },
  inputItem: {
    ph: '2px',
    boxSizing: 'border-box',
    width: '100%',
  },
  button: {
    minWidth: '120px',
    fontWeight: 'bold',
    mt: 1,
    ml: 1,
  },
  label: {
    mt: '7px',
  },
  categoryTitle: {
    borderLeft: 5,
    borderColor: '#0058a0',
    pl: '10px',
  },
  categoryBlock: {
    mt: '5px',
    px: '10px',
    mb: '5px',
  },
  listButton: {
    minWidth: '80px',
    fontWeight: 'bold',
    m: 0,
  },
  headerButton: {
    minWidth: '120px',
    fontWeight: 'bold',
  },
  headerMessage: {
    color: '#d32f2f',
    fontWeight: 'bold',
  },
  fileLink: {
    mt: '7px',
    mr: '10px',
  },
};

const SchoolDecisionApplicationDialog: FC<Props> = ({
  errorAttribute,
  dialogOpen,
  inputData,
  reservedDays,
  handleChangeReservedDay,
  handleChangeSchoolConfirmationDoc,
  handleDeleteSchoolConfirmationDoc,
  handleClose,
  handleRegist,
}) => {
  return (
    <Dialog open={dialogOpen} onClose={handleClose} maxWidth={'md'}>
      <DialogTitle>
        学校決定申請
        {errorAttribute.Header.ErrorMessage && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset" error={errorAttribute.Header.IsError}>
                <FormHelperText sx={{ ml: 0, fontSize: '1.1rem', fontWeight: 'bold' }}>
                  {errorAttribute.Header.ErrorMessage}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </DialogTitle>
      <DialogContent>
        <Typography variant="h6" gutterBottom component="div" sx={styles.categoryTitle}>
          学校決定する日を選択してください。
        </Typography>
        <Grid container spacing={2} sx={styles.categoryBlock}>
          <Grid item xs={3}>
            宿泊日 *
          </Grid>
          <Grid item xs={9}>
            <FormControl required error={errorAttribute.ReservedDays.IsError}>
              {reservedDays.map((x, idx) => (
                <FormControlLabel
                  key={idx}
                  label={x!}
                  control={<Checkbox />}
                  onChange={(e, val) => handleChangeReservedDay(val, idx)}
                />
              ))}
              <FormHelperText>{errorAttribute.ReservedDays.ErrorMessage}</FormHelperText>
            </FormControl>{' '}
            <Typography variant="subtitle2">
              ・別の予約IDに紐づく予約日程を選択したい場合は、予約センターにお問合せ下さい。同一年度、同一学校、同一エリアに限り、予約IDが異なる予約をまとめることができます。
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="h6" gutterBottom component="div" sx={styles.categoryTitle}>
          学校決定通知書をアップロードしてください。
        </Typography>
        <Grid container spacing={2} sx={styles.categoryBlock}>
          <Grid item xs={3}>
            <Typography sx={styles.label}>学校決定通知書 *</Typography>
          </Grid>
          <Grid item xs={9}>
            {!!inputData.SchoolConfirmationDocName && (
              <Link
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.fileLink}
                href={inputData.SchoolConfirmationDoc!}
                download={inputData.SchoolConfirmationDocName}
              >
                {inputData.SchoolConfirmationDocName}
              </Link>
            )}
            {!!inputData.SchoolConfirmationDocName ? (
              <Button variant="outlined" onClick={handleDeleteSchoolConfirmationDoc}>
                削除
              </Button>
            ) : (
              <label htmlFor="schoolConfirmationDoc">
                <input id="schoolConfirmationDoc" type="file" hidden onChange={handleChangeSchoolConfirmationDoc} />
                <Button variant="outlined" component="span">
                  参照
                </Button>
              </label>
            )}
            <FormControl error={errorAttribute.SchoolConfirmationDoc.IsError}>
              <FormHelperText>{errorAttribute.SchoolConfirmationDoc.ErrorMessage}</FormHelperText>
            </FormControl>
            <Typography variant="subtitle2">
              ・アップロードできるファイルは、拡張子が「.doc(.docx)」、「.xls(.xlsx)」、「.pdf」のものに限ります。
            </Typography>
            <Typography variant="subtitle2">・ファイルサイズは、「500KB」以下にしてください。</Typography>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" sx={styles.button} onClick={handleRegist}>
              申請する
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" sx={styles.button} onClick={handleClose}>
              閉じる
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
export default SchoolDecisionApplicationDialog;
